/**
 * Один WebRTC-дзвінок. Уся робота з RTCPeerConnection зібрана тут, щоб медіашар
 * можна було замінити (наприклад, на LiveKit) без змін у контролері чи інтерфейсі.
 *
 * Схема negotiation проста: роль caller/callee призначає сервер, тому offer
 * надсилає лише caller і колізій (glare) не буває.
 */
import type { IceServer, Route, SignalKind } from '@rvc/protocol';

export interface RtcOptions {
  iceServers: IceServer[];
  policy: 'all' | 'relay';
  maxVideoBitrate: number;
}

export interface RtcEvents {
  onSignal(kind: SignalKind, data: unknown): void;
  onRemoteStream(stream: MediaStream): void;
  onConnected(route: Route): void;
  onFailed(): void;
  onStats(data: Record<string, number | string>): void;
}

export class PeerSession {
  private readonly pc: RTCPeerConnection;
  private pending: RTCIceCandidateInit[] = [];
  private remoteReady = false;
  private closed = false;
  private connected = false;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private statsTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly role: 'caller' | 'callee',
    private readonly opts: RtcOptions,
    local: MediaStream,
    private readonly ev: RtcEvents,
  ) {
    this.pc = new RTCPeerConnection({
      iceServers: opts.iceServers,
      iceTransportPolicy: opts.policy,
      bundlePolicy: 'max-bundle',
    });
    for (const track of local.getTracks()) this.pc.addTrack(track, local);

    this.pc.onicecandidate = (e) => {
      if (e.candidate && !this.closed) this.ev.onSignal('candidate', e.candidate.toJSON());
    };
    this.pc.ontrack = (e) => {
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      this.ev.onRemoteStream(stream);
    };
    this.pc.onconnectionstatechange = () => void this.onState();
  }

  /** Caller створює offer одразу; callee чекає на offer від сервера. */
  async start(): Promise<void> {
    if (this.role !== 'caller') return;
    await this.pc.setLocalDescription();
    const desc = this.pc.localDescription;
    if (desc && !this.closed) this.ev.onSignal('offer', desc.toJSON());
  }

  async handleSignal(kind: SignalKind, data: unknown): Promise<void> {
    if (this.closed) return;
    try {
      if (kind === 'offer') {
        await this.pc.setRemoteDescription(data as RTCSessionDescriptionInit);
        await this.flushCandidates();
        await this.pc.setLocalDescription();
        const desc = this.pc.localDescription;
        if (desc && !this.closed) this.ev.onSignal('answer', desc.toJSON());
      } else if (kind === 'answer') {
        await this.pc.setRemoteDescription(data as RTCSessionDescriptionInit);
        await this.flushCandidates();
      } else if (this.remoteReady) {
        await this.pc.addIceCandidate(data as RTCIceCandidateInit);
      } else {
        this.pending.push(data as RTCIceCandidateInit); // candidate раніше offer/answer
      }
    } catch (err) {
      if (!this.closed) {
        console.warn('signal handling failed', kind, err);
        this.ev.onFailed();
      }
    }
  }

  close(): void {
    this.closed = true;
    if (this.disconnectTimer) clearTimeout(this.disconnectTimer);
    if (this.statsTimer) clearInterval(this.statsTimer);
    this.pc.onicecandidate = null;
    this.pc.ontrack = null;
    this.pc.onconnectionstatechange = null;
    this.pc.close();
  }

  // ------------------------------------------------------------ внутрішнє

  private async flushCandidates(): Promise<void> {
    this.remoteReady = true;
    const queued = this.pending;
    this.pending = [];
    for (const c of queued) await this.pc.addIceCandidate(c);
  }

  private async onState(): Promise<void> {
    if (this.closed) return;
    const state = this.pc.connectionState;
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
    if (state === 'connected' && !this.connected) {
      this.connected = true;
      this.capBitrate();
      this.ev.onConnected(await this.detectRoute());
      this.statsTimer = setInterval(() => void this.reportStats(), 15_000);
    } else if (state === 'failed') {
      this.ev.onFailed();
    } else if (state === 'disconnected') {
      // Короткі розриви (перемикання Wi-Fi/LTE) часто відновлюються самі.
      this.disconnectTimer = setTimeout(() => {
        if (!this.closed && this.pc.connectionState !== 'connected') this.ev.onFailed();
      }, 6000);
    }
  }

  /** Обмежуємо відеобітрейт: менше навантаження на TURN і мобільний інтернет. */
  private capBitrate(): void {
    for (const sender of this.pc.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      const params = sender.getParameters();
      if (!params.encodings?.length) params.encodings = [{}];
      params.encodings[0]!.maxBitrate = this.opts.maxVideoBitrate;
      sender.setParameters(params).catch(() => undefined);
    }
  }

  /** direct = пряме з'єднання, relay = трафік іде через TURN. */
  private async detectRoute(): Promise<Route> {
    try {
      const stats = await this.pc.getStats();
      const byId = new Map<string, RTCStats & Record<string, unknown>>();
      stats.forEach((s) => byId.set(s.id, s as RTCStats & Record<string, unknown>));

      let pair: (RTCStats & Record<string, unknown>) | undefined;
      byId.forEach((s) => {
        if (s.type === 'transport' && typeof s.selectedCandidatePairId === 'string') {
          pair = byId.get(s.selectedCandidatePairId);
        }
      });
      if (!pair) {
        byId.forEach((s) => {
          if (s.type === 'candidate-pair' && s.nominated && s.state === 'succeeded') pair = s;
        });
      }
      if (!pair) return 'unknown';
      const local = byId.get(String(pair.localCandidateId));
      const remote = byId.get(String(pair.remoteCandidateId));
      return local?.candidateType === 'relay' || remote?.candidateType === 'relay' ? 'relay' : 'direct';
    } catch {
      return 'unknown';
    }
  }

  private async reportStats(): Promise<void> {
    try {
      const out: Record<string, number | string> = {};
      const stats = await this.pc.getStats();
      stats.forEach((s: Record<string, unknown>) => {
        if (s.type === 'candidate-pair' && s.nominated && typeof s.currentRoundTripTime === 'number') {
          out.rttMs = Math.round(s.currentRoundTripTime * 1000);
        }
        if (s.type === 'inbound-rtp' && s.kind === 'video') {
          if (typeof s.packetsLost === 'number') out.videoPacketsLost = s.packetsLost;
          if (typeof s.framesPerSecond === 'number') out.videoFps = Math.round(s.framesPerSecond);
        }
      });
      if (Object.keys(out).length) this.ev.onStats(out);
    } catch {
      /* статистика необов'язкова */
    }
  }
}
